import { createContext, useContext, useState, PropsWithChildren } from 'react';
import { writeAdminToolsMirror } from '@/context/adminToolsMirror';

export type ViewMode = 'admin' | 'enduser';

export interface ViewModeContextData {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  toggleViewMode: () => void;
  /**
   * The real admin's opt-in to keep seeing Gently admin-only diagnostic
   * surfaces while imitating someone. Off by default so the imitated view
   * stays faithful to what the target actually sees.
   *
   * Only meaningful while `currentUser.imitation` is present — outside
   * imitation `isAdmin` already answers the question. See
   * `isAdminToolsVisible` in `@/utils/user`.
   */
  adminToolsWhileImitating: boolean;
  setAdminToolsWhileImitating: (enabled: boolean) => void;
}

const ViewModeContext = createContext<ViewModeContextData | undefined>(
  undefined,
);

export function ViewModeProvider({ children }: PropsWithChildren) {
  const [viewMode, setViewMode] = useState<ViewMode>('admin');
  const [adminToolsWhileImitating, setAdminToolsWhileImitating] =
    useState(false);

  // Seeded during render rather than in an effect — same as `useAuth` seeding
  // `setImitationState` — so the mirror is never behind the tree that
  // `beforeLoad` is about to run against, and a fresh provider resets it.
  writeAdminToolsMirror(adminToolsWhileImitating);

  const toggleViewMode = () => {
    setViewMode((current) => (current === 'admin' ? 'enduser' : 'admin'));
  };

  return (
    <ViewModeContext.Provider
      value={{
        viewMode,
        setViewMode,
        toggleViewMode,
        adminToolsWhileImitating,
        setAdminToolsWhileImitating,
      }}
    >
      {children}
    </ViewModeContext.Provider>
  );
}

export function useViewMode() {
  const context = useContext(ViewModeContext);
  if (context === undefined) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return context;
}
